import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Calendar, Search, Clock, Loader2 } from "lucide-react";
import { fmtDateFull, fmtRange, fmtTime } from "../lib/dates";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const EmployeePortal = () => {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get("email") || "";
  const [email, setEmail] = useState(initialEmail);
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.get(`${API}/portal/schedule?email=${encodeURIComponent(email.trim())}`);
      setScheduleData(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setScheduleData(null);
      } else {
        toast.error("Failed to look up schedule");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold tracking-tight text-zinc-900">
            roster<span className="text-[#002FA7]">.ai</span>
          </h1>
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500">
            Employee Portal
          </span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Search Section */}
        <div className="bg-white border border-zinc-200 p-8 mb-8">
          <div className="max-w-md">
            <h2 className="text-2xl tracking-tight font-bold text-zinc-900 mb-2">
              View Your Schedule
            </h2>
            <p className="text-sm text-zinc-500 mb-6">
              Enter your email address to look up your published shift schedules.
            </p>
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="portal-email" className="sr-only">Email</Label>
                <Input
                  id="portal-email"
                  type="email"
                  placeholder="your.email@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="portal-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="portal-search-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* Results */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {!loading && searched && !scheduleData && (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-6 h-6 text-zinc-400" />
            </div>
            <h3 className="text-sm font-medium text-zinc-900 mb-1">No schedules found</h3>
            <p className="text-xs text-zinc-500">
              No published schedules were found for this email address.
            </p>
          </div>
        )}

        {!loading && scheduleData && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#002FA7] rounded-full flex items-center justify-center text-white font-bold text-sm">
                {scheduleData.employee_name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
              </div>
              <div>
                <h2 className="text-lg tracking-tight font-bold text-zinc-900">
                  {scheduleData.employee_name}
                </h2>
                <p className="text-xs text-zinc-500">{scheduleData.employee_email}</p>
              </div>
            </div>

            {scheduleData.schedules.length === 0 ? (
              <div className="bg-white border border-zinc-200 p-12 text-center">
                <h3 className="text-sm font-medium text-zinc-900 mb-1">No shifts assigned</h3>
                <p className="text-xs text-zinc-500">You have no shifts in any published rosters.</p>
              </div>
            ) : (
              scheduleData.schedules.map((schedule) => (
                <div key={schedule.roster_id} className="bg-white border border-zinc-200 overflow-hidden">
                  <div className="p-4 border-b border-zinc-200 bg-zinc-50">
                    <h3 className="text-base font-medium text-zinc-900">{schedule.roster_name}</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {fmtRange(schedule.start_date, schedule.end_date)}
                    </p>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-200">
                        <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 text-left py-3 px-4">
                          Date
                        </th>
                        <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 text-left py-3 px-4">
                          Shift
                        </th>
                        <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 text-left py-3 px-4">
                          Time
                        </th>
                        <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 text-left py-3 px-4 hidden sm:table-cell">
                          Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-200">
                      {schedule.entries.map((entry, idx) => (
                        <tr key={idx} className="hover:bg-zinc-50 transition-colors">
                          <td className="py-3 px-4 text-sm tabular-nums text-zinc-900">
                            {fmtDateFull(entry.date)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-sm flex-shrink-0"
                                style={{ backgroundColor: entry.color }}
                              />
                              <span className="text-sm font-medium text-zinc-900">{entry.shift_name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1.5 text-sm tabular-nums text-zinc-600">
                              <Clock className="w-3.5 h-3.5 text-zinc-400" />
                              {fmtTime(entry.start_time)} — {fmtTime(entry.end_time)}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-zinc-500 hidden sm:table-cell">
                            {entry.notes || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-xs text-zinc-400 text-center">
            Powered by roster.ai
          </p>
        </div>
      </footer>
    </div>
  );
};

export default EmployeePortal;
